import { useMemo, Fragment, useState, useEffect, useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";
import Tooltip, { TooltipPrimitive } from "@atlaskit/tooltip";

import { Adg4Button } from "@/components/adg4/button/src";
import { useAdg4TabsContext } from "@/components/adg4/tabs/src";

import {
    CONTEXTUAL_LOGOS,
    DEFAULT_LOGOS,
    FALLBACK_LOGOS
} from "./integrations-logos";

export interface LogoDataType {
    src: string;
    alt: {
        id: string;
        defaultMessage: string;
    };
    tooltipText: {
        id: string;
        defaultMessage: string;
    };
}

export type LogoStateType = [LogoDataType, LogoDataType];

export const Integrations = () => {
    const intl = useIntl();
    const { currentTab } = useAdg4TabsContext();

    const getLeftContextDrivenLogos = useCallback(() => {
        return (
            CONTEXTUAL_LOGOS[currentTab]?.leftRowTwoLogoData ||
            FALLBACK_LOGOS.leftRowTwoLogoData
        );
    }, [currentTab]);

    const getRightContextDrivenLogos = useCallback(() => {
        return (
            CONTEXTUAL_LOGOS[currentTab]?.rightRowTwoLogoData ||
            FALLBACK_LOGOS.rightRowTwoLogoData
        );
    }, [currentTab]);

    const [leftRowTwoData, setLeftRowTwoData] = useState<LogoStateType>(
        getLeftContextDrivenLogos()
    );
    const [rightRowTwoData, setRightRowTwoData] = useState<LogoStateType>(
        getRightContextDrivenLogos()
    );

    useEffect(() => {
        setLeftRowTwoData(getLeftContextDrivenLogos());
        setRightRowTwoData(getRightContextDrivenLogos());
    }, [currentTab, getLeftContextDrivenLogos, getRightContextDrivenLogos]);

    const renderLogos = useMemo(
        () =>
            (
                data: LogoDataType[],
                shouldUseTooltip: boolean,
                toolTipPosition?:
                    | "top-start"
                    | "top-end"
                    | "bottom-start"
                    | "bottom-end"
            ) => {
                return data.map((logoData, i) => {
                    if (shouldUseTooltip) {
                        return (
                            <Fragment key={i}>
                                <Tooltip
                                    position={toolTipPosition}
                                    delay={300}
                                    // @ts-ignore
                                    component={TooltipPrimitive}
                                    content={
                                        <div css={tooltipStyle}>
                                            <p
                                                css={tooltipTextStyle}
                                                className="body-sm"
                                            >
                                                {intl.formatMessage(
                                                    logoData.tooltipText
                                                )}
                                            </p>
                                        </div>
                                    }
                                >
                                    <div
                                        css={logoSquareStyle}
                                        className="logo-square"
                                    >
                                        {/* eslint-disable-next-line no-restricted-syntax */}
                                        <img
                                            src={logoData.src}
                                            alt={intl.formatMessage(
                                                logoData.alt
                                            )}
                                            css={logoImgStyle}
                                        />
                                    </div>
                                </Tooltip>
                            </Fragment>
                        );
                        // render square with no tooltip
                    } else {
                        return (
                            <div key={i}>
                                <div
                                    css={logoSquareStyle}
                                    className="logo-square"
                                >
                                    {/* eslint-disable-next-line no-restricted-syntax */}
                                    <img
                                        src={logoData.src}
                                        alt={intl.formatMessage(logoData.alt)}
                                        css={logoImgStyle}
                                    />
                                </div>
                            </div>
                        );
                    }
                });
            },
        [intl]
    );

    return (
        <UFOSegment name="homepage-v2-integrations">
            <section id="integrations" css={sectionStyle}>
                <div css={outerWrapperStyles}>
                    <div css={mainContentWrapperStyle}>
                        <div css={[logosLeftStyle, logosWrapperStyle]}>
                            <div
                                css={[
                                    logosRowStyle,
                                    logosLeftRowStyle,
                                    logosLeftRowOneStyle
                                ]}
                            >
                                {renderLogos(
                                    DEFAULT_LOGOS.leftRowOneData,
                                    true,
                                    "top-start"
                                )}
                            </div>

                            <div
                                css={[
                                    logosRowStyle,
                                    logosLeftRowStyle,
                                    logosLeftRowTwoStyle
                                ]}
                            >
                                {renderLogos(
                                    leftRowTwoData,
                                    true,
                                    "bottom-start"
                                )}
                            </div>
                        </div>
                        <div css={mainContentContainerStyle}>
                            <h2 css={headerStyles}>
                                {intl.formatMessage({
                                    id: "HomePageTemplateV2.IntegrationsHeader",
                                    defaultMessage:
                                        "Connect your favorite tools with ours"
                                })}
                            </h2>

                            <p className="body-lg" css={pStyles}>
                                {intl.formatMessage({
                                    id: "HomePageTemplateV2.IntegrationsDescription",
                                    defaultMessage:
                                        "Explore thousands of apps and integrations on the Marketplace"
                                })}
                            </p>
                            <Adg4Button
                                href="https://marketplace.atlassian.com/"
                                variant="secondary"
                                color="black"
                                dataEventProps={{
                                    "data-product-key": "home",
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "ctaSection",
                                    "data-label-english": "See all integrations"
                                }}
                            >
                                {intl.formatMessage({
                                    id: "HomePageTemplateV2.IntegrationsCtaButton",
                                    defaultMessage: "See all integrations"
                                })}
                            </Adg4Button>
                        </div>
                        <div css={[logosRightStyles, logosWrapperStyle]}>
                            <div
                                css={[
                                    logosRowStyle,
                                    logosRightRowStyle,
                                    logosRightRowOneStyle
                                ]}
                            >
                                {renderLogos(
                                    DEFAULT_LOGOS.rightRowOneData,
                                    true,
                                    "top-end"
                                )}
                            </div>

                            <div
                                css={[
                                    logosRowStyle,
                                    logosRightRowStyle,
                                    logosRightRowTwoStyle
                                ]}
                            >
                                {renderLogos(
                                    rightRowTwoData,
                                    true,
                                    "bottom-end"
                                )}
                            </div>
                        </div>
                    </div>

                    <div css={[logosMobileStyle, logosWrapperStyle]}>
                        <div css={[logosRowStyle, logosMobileRowStyle]}>
                            {renderLogos(DEFAULT_LOGOS.leftRowOneData, false)}
                        </div>

                        <div css={[logosRowStyle, logosMobileRowStyle]}>
                            {renderLogos(
                                [...leftRowTwoData, ...rightRowTwoData],
                                false
                            )}
                        </div>

                        <div css={[logosRowStyle, logosMobileRowStyle]}>
                            {renderLogos(DEFAULT_LOGOS.rightRowOneData, false)}
                        </div>
                    </div>
                </div>
            </section>
        </UFOSegment>
    );
};

const sectionStyle = css({
    marginBlockStart: "40px",
    marginBlockEnd: "40px",
    paddingBlockStart: "20px",
    paddingBlockEnd: "20px"
});

const outerWrapperStyles = css({
    paddingBlockStart: "170px",
    paddingBlockEnd: "192px",
    maxWidth: "1440px",
    position: "relative", // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#F8F8F8",
    clipPath:
        "polygon(100% 0,100% 94%,42% 100%,41.7% 94%,0 94%,0 16%,30% 10%,30% 4%)",
    overflow: "hidden",
    justifyContent: "center",
    display: "flex",
    margin: "0 auto",

    [media.below.md]: {
        flexDirection: "column",
        alignItems: "center",
        gap: "40px",
        paddingBlockStart: "160px",
        paddingBlockEnd: "130px"
    }
});

const mainContentWrapperStyle = css({
    position: "relative",
    [media.above.md]: {
        flex: "0 0 33%"
    }
});

const mainContentContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: `${token("space.250")}`,
    textAlign: "center",
    maxWidth: "440px",
    minWidth: "320px",
    paddingInlineStart: "20px",
    paddingInlineEnd: "20px",

    [media.above.md]: {
        gap: `${token("space.400")}`
    }
});

const headerStyles = css({
    marginBlockStart: 0,
    marginBlockEnd: 0
});

const pStyles = css({
    marginBlockStart: 0,
    marginBlockEnd: 0
});

const logosRowStyle = css({
    display: "flex",
    gap: "40px",

    [media.below.md]: {
        gap: "27px"
    }
});

const tooltipStyle = css({
    position: "relative",
    padding: "10px 16px",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    borderRadius: "6px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "1px solid #FFC400",
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#FFF",
    boxShadow:
        // eslint-disable-next-line local/ensure-wac-token-usage
        "0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 8px 12px 0px rgba(9, 30, 66, 0.15)",
    maxWidth: "240px"
});

const tooltipTextStyle = css({
    marginBlock: "0"
});

const logosWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    paddingBlockEnd: "2px",
    [media.above.md]: {
        position: "absolute",
        width: "422px",
        insetBlockStart: "50%"
    }
});

const logosLeftStyle = css({
    flex: "1 1 100%",
    insetInlineStart: "0",
    transform: "translate(-100%, -50%)",

    [media.below.md]: {
        display: "none"
    }
});

const logosLeftRowStyle = css({
    justifyContent: "flex-end"
});

const logosLeftRowOneStyle = css({
    position: "relative",
    insetInlineEnd: "6px"
});

const logosLeftRowTwoStyle = css({
    paddingBlockStart: "40px",
    position: "relative",
    insetInlineStart: "-14px"
});

const logosRightStyles = css({
    justifyContent: "flex-end",
    insetInlineEnd: "0%",
    flex: "1 1 100%",
    transform: "translate(100%, -50%)",

    [media.below.md]: {
        display: "none"
    }
});

const logosRightRowStyle = css({
    justifyContent: "flex-start"
});

const logosRightRowOneStyle = css({
    position: "relative",
    insetInlineEnd: "-6px"
});

const logosRightRowTwoStyle = css({
    paddingBlockStart: "40px",
    position: "relative",
    insetInlineEnd: "-14px"
});

const logosMobileStyle = css({
    justifyContent: "center",
    padding: "0 40px",
    width: "100%",
    gap: "40px",

    [media.above.md]: {
        display: "none"
    }
});

const logosMobileRowStyle = css({
    justifyContent: "center"
});

const logoSquareStyle = css({
    aspectRatio: "1 / 1",
    flex: "0 0 114px",
    maxWidth: "114px",
    maxHeight: "114px",

    width: "100%",
    height: "100%",
    borderRadius: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    // eslint-disable-next-line local/ensure-wac-token-usage
    boxShadow: "0px 0px 1px 0px #a1aab7, 0px 0px 4px 0px #a1aab7",

    [media.below.md]: {
        flex: "0 0 75px",
        width: "75px",
        height: "75px",
        cursor: "default",
        borderRadius: "12px"
    }
});

const logoImgStyle = css({
    width: "114px",
    height: "auto",

    [media.below.md]: {
        width: "100%"
    }
});
